import { useCallback, useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { api, ApiError, type Issue } from '../api';
import { EMPTY_FORM, SaleForm } from '../components/SaleForm';
import { TopBar } from '../components/TopBar';
import { TYPE_LABEL, whenLong } from '../format';

/** Mirrors src/server/db/types.ts's ADMIN_ROLES -- the client and server
 * intentionally don't share type imports (see the tsconfig.client.json /
 * tsconfig.json split), matching how other server-defined string unions
 * (event_type, account_type) are already duplicated on this side. */
/** Draws the otpauth URL as a QR code in the browser, so the 2FA secret is
 * never sent to a third-party QR service. */
function TotpQr({ url }: { url: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(url, { width: 220, margin: 1 })
      .then((d) => live && setSrc(d))
      .catch(() => live && setSrc(null));
    return () => {
      live = false;
    };
  }, [url]);
  return src ? <img src={src} alt="QR code for your authenticator app" width={220} height={220} /> : null;
}

type AdminRole = 'administrator' | 'moderator' | 'newsletter_manager' | 'sponsor_manager';

interface AdminEvent {
  id: string;
  public_id: string;
  event_type: string;
  status: string;
  title: string;
  address_1: string;
  city: string;
  state: string;
  postal_code: string;
  start_at: string;
  end_at: string;
  end_time_unknown: boolean;
  timezone: string;
  flag_reason: string | null;
  flag_count: number;
  submitted_at: string;
  featured_tier: string | null;
  featured_at: string | null;
  contact: { name: string; phone: string; email: string; newsletter_opt_in: boolean } | null;
}

interface AddResult {
  public_id: string;
  listing_url: string;
  edit_url: string;
  emailed: boolean;
  approximate_location: boolean;
}

interface NewsletterSummary {
  by_status: Record<string, number>;
  mailing_address: string;
  mailing_address_set: boolean;
  send_hour: number;
  paused: boolean;
}

interface DigestListing {
  public_id: string;
  title: string;
  city: string;
  state: string;
  start_at: string;
  distance_mi?: number;
}

interface DigestPreview {
  listings: DigestListing[];
  total: number;
}

interface AdminUser {
  id: string;
  email: string;
  status: 'active' | 'disabled';
  account_type: 'residential' | 'commercial' | null;
  created_at: string;
  last_sign_in_at: string | null;
  subscribed: boolean;
  route_count: number;
  listing_count: number;
}

interface AdminShopPlace {
  id: string;
  public_id: string;
  name: string;
  category: string;
  address_1: string;
  city: string;
  state: string;
  postal_code: string;
  status: string;
  owner_email: string | null;
  submitted_at: string;
  flag_count: number;
}

const TABS = [
  { key: 'flagged', label: 'Needs review' },
  { key: 'published', label: 'Published' },
  { key: '', label: 'All' },
];

const REASONS: Record<string, string> = {
  geocode_failed: "Address couldn't be placed on the map (pinned at ZIP center)",
  too_many_urls: 'Lots of links',
  blocklist: 'Spam words',
};

export function Admin() {
  const [me, setMe] = useState<string | null | undefined>(undefined);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [section, setSection] = useState<
    'listings' | 'newsletter' | 'users' | 'places' | 'signals' | 'listing-boost' | 'campaigns' | 'sponsors' | 'analytics'
  >('listings');
  const [tab, setTab] = useState('flagged');
  const [q, setQ] = useState('');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [rows, setRows] = useState<AdminEvent[]>([]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addIssues, setAddIssues] = useState<Issue[]>([]);
  const [addMsg, setAddMsg] = useState('');
  const [added, setAdded] = useState<AddResult | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [role, setRole] = useState<AdminRole | null>(null);
  const [authStep, setAuthStep] = useState<'password' | 'totp' | 'enroll'>('password');
  const [totpCode, setTotpCode] = useState('');
  const [enroll, setEnroll] = useState<{ secret: string; otpauth_url: string } | null>(null);

  useEffect(() => {
    document.title = 'Admin — The Yard Sale Map';
    api
      .get<{ email: string; role: AdminRole }>('/admin/me')
      .then((r) => {
        setMe(r.email);
        setRole(r.role);
      })
      .catch(() => setMe(null));
  }, []);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (tab) p.set('status', tab);
    if (q.trim()) p.set('q', q.trim());
    if (featuredOnly) p.set('featured', 'true');
    api
      .get<AdminEvent[]>(`/admin/events?${p}`)
      .then(setRows)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) setMe(null);
      });
  }, [tab, q, featuredOnly]);

  useEffect(() => {
    if (me) load();
  }, [me, load]);

  // Defense-in-depth only -- the server enforces every route's actual role
  // restriction (requireRole in admin.ts); this just keeps a role from
  // seeing tabs their own account can't do anything with.
  const SECTIONS_BY_ROLE: Record<AdminRole, (typeof section)[]> = {
    administrator: ['listings', 'newsletter', 'users', 'places', 'signals', 'campaigns', 'sponsors', 'listing-boost', 'analytics'],
    moderator: ['listings', 'users', 'places'],
    newsletter_manager: ['newsletter', 'signals'],
    sponsor_manager: ['campaigns', 'sponsors'],
  };
  const visibleSections = role ? SECTIONS_BY_ROLE[role] : [];

  useEffect(() => {
    if (role && !visibleSections.includes(section) && visibleSections[0]) setSection(visibleSections[0]);
    // visibleSections is derived fresh each render from role; only role should re-trigger this.
  }, [role]);

  async function login(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    try {
      const r = await api.postJson<{ email?: string; requires_totp?: boolean; requires_totp_enrollment?: boolean }>(
        '/admin/login',
        { email, password },
      );
      setPassword('');
      if (r.requires_totp) {
        setAuthStep('totp');
        return;
      }
      if (r.requires_totp_enrollment) {
        const e2fa = await api.get<{ secret: string; otpauth_url: string }>('/admin/2fa/enroll');
        setEnroll(e2fa);
        setAuthStep('enroll');
        return;
      }
      const me2 = await api.get<{ email: string; role: AdminRole }>('/admin/me');
      setMe(me2.email);
      setRole(me2.role);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Sign-in failed');
    }
  }

  async function submitTotp(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    try {
      await api.postJson('/admin/login/totp', { code: totpCode });
      setTotpCode('');
      setAuthStep('password');
      const me2 = await api.get<{ email: string; role: AdminRole }>('/admin/me');
      setMe(me2.email);
      setRole(me2.role);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "That code didn't work");
    }
  }

  async function confirmEnroll(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    try {
      await api.postJson('/admin/2fa/enroll/confirm', { code: totpCode });
      setTotpCode('');
      setAuthStep('password');
      setEnroll(null);
      const me2 = await api.get<{ email: string; role: AdminRole }>('/admin/me');
      setMe(me2.email);
      setRole(me2.role);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "That code didn't work");
    }
  }

  async function setStatus(id: string, status: string) {
    setBusy(true);
    try {
      await fetch(`/api/v1/admin/events/${id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      load();
    } finally {
      setBusy(false);
    }
  }

  async function addSale(fields: Record<string, string>, photos: File[]) {
    setBusy(true);
    setAddIssues([]);
    setAddMsg('');
    const fd = new FormData();
    for (const [k, val] of Object.entries(fields)) fd.append(k, val);
    for (const p of photos) fd.append('photos', p, p.name);
    try {
      const r = await api.postForm<AddResult>('/admin/events', fd);
      setAdded(r);
      setFormKey((k) => k + 1);
      load();
      window.scrollTo(0, 0);
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 401) setMe(null);
        setAddIssues(e.issues);
        setAddMsg(e.message);
      } else setAddMsg('Something went wrong adding the sale.');
    } finally {
      setBusy(false);
    }
  }

  if (me === undefined) return null;

  if (!me && authStep === 'totp') {
    return (
      <>
        <TopBar />
        <main className="page" style={{ maxWidth: 420 }}>
          <h1>Enter your code</h1>
          <p className="hint">Open your authenticator app and enter the current 6-digit code.</p>
          <form onSubmit={(e) => void submitTotp(e)}>
            <label htmlFor="ad-totp">Code</label>
            <input
              id="ad-totp"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value)}
            />
            <p className="error" role="alert">
              {err}
            </p>
            <button className="primary" type="submit">
              Verify
            </button>
          </form>
        </main>
      </>
    );
  }

  if (!me && authStep === 'enroll' && enroll) {
    return (
      <>
        <TopBar />
        <main className="page" style={{ maxWidth: 420 }}>
          <h1>Set up two-factor authentication</h1>
          <p className="hint">
            This role requires it. Add this account to an authenticator app (Google
            Authenticator, 1Password, Authy) — scan the QR code below or type the secret in
            manually — then enter the code it shows.
          </p>
          <p>
            <TotpQr url={enroll.otpauth_url} />
          </p>
          <p>
            Secret: <code>{enroll.secret}</code>
          </p>
          <form onSubmit={(e) => void confirmEnroll(e)}>
            <label htmlFor="ad-enroll-code">Code</label>
            <input
              id="ad-enroll-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value)}
            />
            <p className="error" role="alert">
              {err}
            </p>
            <button className="primary" type="submit">
              Confirm and finish signing in
            </button>
          </form>
        </main>
      </>
    );
  }

  if (!me) {
    return (
      <>
        <TopBar />
        <main className="page" style={{ maxWidth: 420 }}>
          <h1>Admin sign in</h1>
          <form onSubmit={(e) => void login(e)}>
            <label htmlFor="ad-email">Email</label>
            <input
              id="ad-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label htmlFor="ad-pw">Password</label>
            <input
              id="ad-pw"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="error" role="alert">
              {err}
            </p>
            <button className="primary" type="submit">
              Sign in
            </button>
          </form>
        </main>
      </>
    );
  }

  return (
    <>
      <TopBar />
      <main className="page" style={{ maxWidth: 1100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 style={{ flex: 1, margin: 0 }}>
            {section === 'listings'
              ? 'Listings'
              : section === 'newsletter'
                ? 'Newsletter'
                : section === 'users'
                  ? 'Users'
                  : section === 'places'
                    ? 'Places'
                    : section === 'signals'
                      ? 'Signals'
                      : section === 'campaigns'
                        ? 'Campaign links'
                        : section === 'sponsors'
                          ? 'Sponsors'
                          : 'Payment settings'}
          </h1>
          <span className="hint">{me}</span>
          <button
            className="link"
            onClick={() => {
              void api.postJson('/admin/logout').finally(() => setMe(null));
            }}
          >
            Sign out
          </button>
        </div>
        <div className="tabs" role="group" aria-label="Section" style={{ marginBottom: 16 }}>
          {visibleSections.includes('listings') && (
            <button aria-pressed={section === 'listings'} onClick={() => setSection('listings')}>
              Listings
            </button>
          )}
          {visibleSections.includes('newsletter') && (
            <button aria-pressed={section === 'newsletter'} onClick={() => setSection('newsletter')}>
              Newsletter
            </button>
          )}
          {visibleSections.includes('users') && (
            <button aria-pressed={section === 'users'} onClick={() => setSection('users')}>
              Users
            </button>
          )}
          {visibleSections.includes('places') && (
            <button aria-pressed={section === 'places'} onClick={() => setSection('places')}>
              Places
            </button>
          )}
          {visibleSections.includes('signals') && (
            <button aria-pressed={section === 'signals'} onClick={() => setSection('signals')}>
              Signals
            </button>
          )}
          {visibleSections.includes('campaigns') && (
            <button aria-pressed={section === 'campaigns'} onClick={() => setSection('campaigns')}>
              Campaign links
            </button>
          )}
          {visibleSections.includes('sponsors') && (
            <button aria-pressed={section === 'sponsors'} onClick={() => setSection('sponsors')}>
              Sponsors
            </button>
          )}
          {visibleSections.includes('listing-boost') && (
            <button
              aria-pressed={section === 'listing-boost'}
              onClick={() => setSection('listing-boost')}
            >
              Payment settings
            </button>
          )}
          {visibleSections.includes('analytics') && (
            <button aria-pressed={section === 'analytics'} onClick={() => setSection('analytics')}>
              Analytics
            </button>
          )}
        </div>
        {section === 'newsletter' ? (
          <NewsletterSection />
        ) : section === 'users' ? (
          <UsersSection />
        ) : section === 'places' ? (
          <PlacesSection />
        ) : section === 'signals' ? (
          <SignalsSection />
        ) : section === 'campaigns' ? (
          <CampaignLinksSection />
        ) : section === 'sponsors' ? (
          <SponsorsSection canEditPrices={role === 'administrator'} />
        ) : section === 'listing-boost' ? (
          <ListingBoostSection />
        ) : section === 'analytics' ? (
          <AnalyticsSection />
        ) : (
          <>
            {added && (
              <div className="info" role="status">
                <p style={{ margin: '0 0 6px' }}>
                  Sale added and live:{' '}
                  <a href={new URL(added.listing_url).pathname}>
                    {new URL(added.listing_url).pathname}
                  </a>
                </p>
                {added.approximate_location && (
                  <p style={{ margin: '0 0 6px' }}>
                    ⚠ The street address couldn't be placed, so the pin is at the ZIP code's center.
                    Check it on the map.
                  </p>
                )}
                <p style={{ margin: 0 }}>
                  {added.emailed
                    ? 'The host was emailed their private edit link. '
                    : 'No email was sent — send the host this private edit link: '}
                </p>
                <div className="copybox">
                  <input
                    readOnly
                    value={added.edit_url}
                    aria-label="Host's private edit link"
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    type="button"
                    onClick={() => void navigator.clipboard?.writeText(added.edit_url)}
                  >
                    Copy
                  </button>
                </div>
                <button type="button" className="link" onClick={() => setAdded(null)}>
                  Dismiss
                </button>
              </div>
            )}
            <p>
              <button
                type="button"
                className={adding ? '' : 'primary'}
                onClick={() => setAdding((a) => !a)}
                aria-expanded={adding}
              >
                {adding ? 'Close the add form' : '+ Add a sale'}
              </button>
            </p>
            {adding && (
              <section className="card" style={{ marginBottom: 20 }} aria-labelledby="add-h">
                <h2 id="add-h">Add a sale for a host</h2>
                <p className="hint">
                  For sales sent to addmysale@theyardsalemap.com. It goes live immediately — no bot
                  check or limits.
                </p>
                {addMsg && (
                  <p className="error" role="alert">
                    {addMsg}
                  </p>
                )}
                <SaleForm
                  key={formKey}
                  mode="admin"
                  initial={EMPTY_FORM}
                  issues={addIssues}
                  busy={busy}
                  onSubmit={(f, p) => void addSale(f, p)}
                />
              </section>
            )}
            <div className="tabs" role="group" aria-label="Status">
              {TABS.map((t) => (
                <button key={t.key} aria-pressed={tab === t.key} onClick={() => setTab(t.key)}>
                  {t.label}
                </button>
              ))}
              <button aria-pressed={featuredOnly} onClick={() => setFeaturedOnly((f) => !f)}>
                Featured only
              </button>
            </div>
            <form
              className="search-row"
              onSubmit={(e) => {
                e.preventDefault();
                load();
              }}
            >
              <input
                placeholder="Search title, city, ZIP or listing code"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <button type="submit">Search</button>
            </form>
            {rows.length === 0 ? (
              <p className="empty">Nothing here.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Sale</th>
                      <th>When / where</th>
                      <th>Host (private)</th>
                      <th>Status</th>
                      <th>Featured</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <a href={`/e/${r.public_id}`} target="_blank" rel="noopener">
                            {r.title}
                          </a>
                          <br />
                          <span className="hint">
                            {TYPE_LABEL[r.event_type]} · {r.public_id}
                          </span>
                        </td>
                        <td>
                          {whenLong(r.start_at, r.end_at, r.timezone, r.end_time_unknown)}
                          <br />
                          {r.address_1}, {r.city}, {r.state} {r.postal_code}
                        </td>
                        <td>
                          {r.contact ? (
                            <>
                              {r.contact.name}
                              <br />
                              <a href={`tel:${r.contact.phone}`}>{r.contact.phone}</a>
                              <br />
                              <a href={`mailto:${r.contact.email}`}>{r.contact.email}</a>
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          <strong>{r.status}</strong>
                          {r.flag_reason && (
                            <div className="hint">
                              {r.flag_reason
                                .split(',')
                                .map((x) => REASONS[x] ?? x)
                                .join('; ')}
                            </div>
                          )}
                          {r.flag_count > 0 && (
                            <div className="hint">
                              {r.flag_count} shopper report{r.flag_count === 1 ? '' : 's'}
                            </div>
                          )}
                        </td>
                        <td>
                          {r.featured_tier ?? '—'}
                          {r.featured_tier && r.featured_at && (
                            <div className="hint">
                              {new Date(r.featured_at).toLocaleDateString()}
                            </div>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {r.status !== 'published' && (
                            <button
                              disabled={busy}
                              onClick={() => void setStatus(r.id, 'published')}
                            >
                              Publish
                            </button>
                          )}{' '}
                          {r.status !== 'rejected' && (
                            <button
                              disabled={busy}
                              onClick={() => void setStatus(r.id, 'rejected')}
                            >
                              Reject
                            </button>
                          )}{' '}
                          {r.status === 'published' && (
                            <button disabled={busy} onClick={() => void setStatus(r.id, 'flagged')}>
                              Hide
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}

function NewsletterSection() {
  const [summary, setSummary] = useState<NewsletterSummary | null>(null);
  const [mailingAddress, setMailingAddress] = useState('');
  const [sendHour, setSendHour] = useState(7);
  const [paused, setPaused] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const [postalCode, setPostalCode] = useState('');
  const [radiusMi, setRadiusMi] = useState(25);
  const [preview, setPreview] = useState<DigestPreview | null>(null);
  const [previewErr, setPreviewErr] = useState('');
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(() => {
    api
      .get<NewsletterSummary>('/admin/newsletter/summary')
      .then((s) => {
        setSummary(s);
        setMailingAddress(s.mailing_address);
        setSendHour(s.send_hour);
        setPaused(s.paused);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function saveSettings(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveMsg('');
    try {
      await api.putJson('/admin/newsletter/settings', {
        mailing_address: mailingAddress,
        send_hour: sendHour,
        paused,
      });
      setSaveMsg('Saved.');
      load();
    } catch (e2) {
      setSaveMsg(e2 instanceof ApiError ? e2.message : 'Could not save settings.');
    } finally {
      setSaving(false);
    }
  }

  async function runPreview(e: React.FormEvent) {
    e.preventDefault();
    setPreviewing(true);
    setPreviewErr('');
    setPreview(null);
    try {
      const p = new URLSearchParams({ postal_code: postalCode, radius_mi: String(radiusMi) });
      const r = await api.get<DigestPreview>(`/admin/newsletter/preview?${p}`);
      setPreview(r);
    } catch (e2) {
      setPreviewErr(e2 instanceof ApiError ? e2.message : 'Could not build a preview.');
    } finally {
      setPreviewing(false);
    }
  }

  return (
    <>
      <section className="card" style={{ marginBottom: 20 }}>
        <h2>Subscribers</h2>
        {summary ? (
          <p>
            {Object.entries(summary.by_status).map(([status, n]) => (
              <span key={status} style={{ marginRight: 16 }}>
                <strong>{n}</strong> {status}
              </span>
            ))}
            {Object.keys(summary.by_status).length === 0 && (
              <span className="hint">No subscribers yet.</span>
            )}
          </p>
        ) : (
          <p className="hint">Loading…</p>
        )}
        {summary && !summary.mailing_address_set && (
          <p className="error" role="alert">
            No mailing address is set — the send job refuses to send any mail until one is set below
            (CAN-SPAM requires a postal address in every email).
          </p>
        )}
      </section>

      <section className="card" style={{ marginBottom: 20 }}>
        <h2>Settings</h2>
        <form onSubmit={(e) => void saveSettings(e)}>
          <label htmlFor="nl-address">Mailing address (shown in every email footer)</label>
          <textarea
            id="nl-address"
            rows={2}
            value={mailingAddress}
            onChange={(e) => setMailingAddress(e.target.value)}
          />
          <label htmlFor="nl-hour">Send hour (0–23, in each subscriber's own local time)</label>
          <input
            id="nl-hour"
            type="number"
            min={0}
            max={23}
            value={sendHour}
            onChange={(e) => setSendHour(Number(e.target.value))}
            style={{ width: 80 }}
          />
          <p>
            <label>
              <input
                type="checkbox"
                checked={paused}
                onChange={(e) => setPaused(e.target.checked)}
              />{' '}
              Pause the newsletter (stop queuing new weeks; already-queued mail still sends)
            </label>
          </p>
          {saveMsg && <p role="status">{saveMsg}</p>}
          <button className="primary" type="submit" disabled={saving}>
            Save
          </button>
        </form>
      </section>

      <section className="card">
        <h2>Preview a digest</h2>
        <p className="hint">See what a subscriber at a given ZIP/radius would receive this week.</p>
        <form className="search-row" onSubmit={(e) => void runPreview(e)}>
          <input
            placeholder="ZIP code"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            style={{ maxWidth: 120 }}
          />
          <input
            type="number"
            placeholder="Radius (mi)"
            value={radiusMi}
            onChange={(e) => setRadiusMi(Number(e.target.value))}
            style={{ maxWidth: 120 }}
          />
          <button type="submit" disabled={previewing}>
            Preview
          </button>
        </form>
        {previewErr && (
          <p className="error" role="alert">
            {previewErr}
          </p>
        )}
        {preview && (
          <>
            <p className="hint">
              {preview.total} match{preview.total === 1 ? '' : 'es'} this weekend
              {preview.total > preview.listings.length
                ? ` (showing ${preview.listings.length})`
                : ''}
              .
            </p>
            {preview.listings.length === 0 ? (
              <p className="empty">Nothing this weekend for this ZIP/radius.</p>
            ) : (
              <ul>
                {preview.listings.map((l) => (
                  <li key={l.public_id}>
                    {l.title} — {l.city}, {l.state}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>
    </>
  );
}

function UsersSection() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<AdminUser[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q.trim()) p.set('q', q.trim());
    api
      .get<AdminUser[]>(`/admin/users?${p}`)
      .then(setRows)
      .catch(() => {});
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  async function disable(id: string) {
    setBusy(id);
    setMsg('');
    try {
      await api.postJson(`/admin/users/${id}/disable`);
      load();
    } catch {
      setMsg('Could not disable that user.');
    } finally {
      setBusy(null);
    }
  }

  async function enable(id: string) {
    setBusy(id);
    setMsg('');
    try {
      await api.postJson(`/admin/users/${id}/enable`);
      load();
    } catch {
      setMsg('Could not enable that user.');
    } finally {
      setBusy(null);
    }
  }

  async function remove(id: string, email: string) {
    if (
      !window.confirm(
        `Permanently delete ${email}? This also deletes their routes and subscription. This cannot be undone.`,
      )
    )
      return;
    setBusy(id);
    setMsg('');
    try {
      await api.delete(`/admin/users/${id}`);
      load();
    } catch {
      setMsg('Could not delete that user.');
    } finally {
      setBusy(null);
    }
  }

  async function sendLink(id: string) {
    setBusy(id);
    setMsg('');
    try {
      await api.postJson(`/admin/users/${id}/send-link`);
      setMsg('Sign-in link sent.');
    } catch {
      setMsg('Could not send a sign-in link.');
    } finally {
      setBusy(null);
    }
  }

  async function correctAccountType(id: string, accountType: string) {
    setBusy(id);
    setMsg('');
    try {
      await api.patchJson(`/admin/users/${id}/account-type`, { account_type: accountType });
      load();
    } catch {
      setMsg('Could not update account type.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <form
        className="search-row"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
      >
        <input placeholder="Search by email" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="submit">Search</button>
      </form>
      {msg && (
        <p role="status" className="hint">
          {msg}
        </p>
      )}
      {rows.length === 0 ? (
        <p className="empty">No users found.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Account type</th>
                <th>Joined</th>
                <th>Last sign-in</th>
                <th>Subscribed</th>
                <th>Routes</th>
                <th>Listings</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.email}</td>
                  <td>
                    <select
                      disabled={busy === r.id}
                      value={r.account_type ?? ''}
                      onChange={(e) => void correctAccountType(r.id, e.target.value)}
                    >
                      <option value="" disabled>
                        (not set)
                      </option>
                      <option value="residential">Residential</option>
                      <option value="commercial">Commercial</option>
                    </select>
                  </td>
                  <td>{new Date(r.created_at).toLocaleDateString()}</td>
                  <td>
                    {r.last_sign_in_at ? new Date(r.last_sign_in_at).toLocaleDateString() : '—'}
                  </td>
                  <td>{r.subscribed ? 'Yes' : 'No'}</td>
                  <td>{r.route_count}</td>
                  <td>{r.listing_count}</td>
                  <td>
                    <strong>{r.status}</strong>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {r.status === 'active' ? (
                      <button disabled={busy === r.id} onClick={() => void disable(r.id)}>
                        Disable
                      </button>
                    ) : (
                      <button disabled={busy === r.id} onClick={() => void enable(r.id)}>
                        Enable
                      </button>
                    )}{' '}
                    <button disabled={busy === r.id} onClick={() => void sendLink(r.id)}>
                      Send sign-in link
                    </button>{' '}
                    <button disabled={busy === r.id} onClick={() => void remove(r.id, r.email)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function PlacesSection() {
  const [rows, setRows] = useState<AdminShopPlace[]>([]);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: '',
    category: '',
    address_1: '',
    city: '',
    state: '',
    postal_code: '',
    description: '',
    website_url: '',
    phone: '',
  });
  const [addMsg, setAddMsg] = useState('');

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (q.trim()) p.set('q', q.trim());
    api
      .get<AdminShopPlace[]>(`/admin/shop-places?${p}`)
      .then(setRows)
      .catch(() => {});
  }, [status, q]);

  useEffect(() => {
    load();
  }, [load]);

  async function setPlaceStatus(id: string, next: string) {
    setBusy(id);
    try {
      await api.patchJson(`/admin/shop-places/${id}`, { status: next });
      load();
    } finally {
      setBusy(null);
    }
  }

  async function addPlace(e: React.FormEvent) {
    e.preventDefault();
    setAddMsg('');
    try {
      await api.postJson('/admin/shop-places', form);
      setForm({
        name: '',
        category: 'antique',
        address_1: '',
        city: '',
        state: '',
        postal_code: '',
        description: '',
        website_url: '',
        phone: '',
      });
      setAdding(false);
      load();
    } catch (e2) {
      setAddMsg(e2 instanceof ApiError ? e2.message : 'Could not add that place.');
    }
  }

  return (
    <>
      <p>
        <button
          type="button"
          className={adding ? '' : 'primary'}
          onClick={() => setAdding((a) => !a)}
          aria-expanded={adding}
        >
          {adding ? 'Close the add form' : '+ Add a place'}
        </button>
      </p>
      {adding && (
        <section className="card" style={{ marginBottom: 20 }}>
          <h2>Add a place</h2>
          {addMsg && <p className="error">{addMsg}</p>}
          <form onSubmit={(e) => void addPlace(e)}>
            <label htmlFor="ap-name">Name</label>
            <input
              id="ap-name"
              required
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            />
            <label htmlFor="ap-category">Category</label>
            <input
              id="ap-category"
              list="ap-category-suggestions"
              required
              placeholder="e.g. Antique shop, Farm stand, Bakery"
              value={form.category}
              onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))}
            />
            <datalist id="ap-category-suggestions">
              <option value="Antique shop" />
              <option value="Thrift store" />
              <option value="Consignment shop" />
              <option value="Farm stand" />
              <option value="Bakery" />
              <option value="Bookstore" />
              <option value="Flea market" />
            </datalist>
            <label htmlFor="ap-addr">Street address</label>
            <input
              id="ap-addr"
              required
              value={form.address_1}
              onChange={(e) => setForm((s) => ({ ...s, address_1: e.target.value }))}
            />
            <div className="row3">
              <div>
                <label htmlFor="ap-city">City</label>
                <input
                  id="ap-city"
                  required
                  value={form.city}
                  onChange={(e) => setForm((s) => ({ ...s, city: e.target.value }))}
                />
              </div>
              <div>
                <label htmlFor="ap-state">State</label>
                <input
                  id="ap-state"
                  required
                  maxLength={2}
                  value={form.state}
                  onChange={(e) => setForm((s) => ({ ...s, state: e.target.value }))}
                />
              </div>
              <div>
                <label htmlFor="ap-zip">ZIP</label>
                <input
                  id="ap-zip"
                  required
                  value={form.postal_code}
                  onChange={(e) => setForm((s) => ({ ...s, postal_code: e.target.value }))}
                />
              </div>
            </div>
            <label htmlFor="ap-website">Website</label>
            <input
              id="ap-website"
              value={form.website_url}
              onChange={(e) => setForm((s) => ({ ...s, website_url: e.target.value }))}
            />
            <label htmlFor="ap-phone">Phone</label>
            <input
              id="ap-phone"
              value={form.phone}
              onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
            />
            <button className="primary" type="submit">
              Add
            </button>
          </form>
        </section>
      )}
      <div className="tabs" role="group" aria-label="Status">
        {['', 'published', 'flagged', 'closed', 'rejected'].map((s) => (
          <button key={s} aria-pressed={status === s} onClick={() => setStatus(s)}>
            {s || 'All'}
          </button>
        ))}
      </div>
      <form
        className="search-row"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
      >
        <input
          placeholder="Search name, city, ZIP or listing code"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="submit">Search</button>
      </form>
      {rows.length === 0 ? (
        <p className="empty">Nothing here.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Shop</th>
                <th>Address</th>
                <th>Status</th>
                <th>Flags</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <a href={`/place/${r.public_id}`} target="_blank" rel="noopener">
                      {r.name}
                    </a>
                    <br />
                    <span className="hint">
                      {r.category} · {r.public_id}
                    </span>
                  </td>
                  <td>
                    {r.address_1}, {r.city}, {r.state} {r.postal_code}
                  </td>
                  <td>
                    <strong>{r.status}</strong>
                  </td>
                  <td>{r.flag_count}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {r.status !== 'published' && (
                      <button
                        disabled={busy === r.id}
                        onClick={() => void setPlaceStatus(r.id, 'published')}
                      >
                        Publish / dismiss flags
                      </button>
                    )}{' '}
                    {r.status !== 'closed' && (
                      <button
                        disabled={busy === r.id}
                        onClick={() => void setPlaceStatus(r.id, 'closed')}
                      >
                        Confirm closed
                      </button>
                    )}{' '}
                    {r.status !== 'rejected' && (
                      <button
                        disabled={busy === r.id}
                        onClick={() => void setPlaceStatus(r.id, 'rejected')}
                      >
                        Reject
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

interface CampaignLinkRow {
  id: string;
  slug: string;
  destination_path: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  click_count: number;
  created_at: string;
}

const CAMPAIGN_FORM_EMPTY = {
  slug: '',
  destination_path: '/',
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_content: '',
};

function CampaignLinksSection() {
  const [rows, setRows] = useState<CampaignLinkRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState(CAMPAIGN_FORM_EMPTY);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get<CampaignLinkRow[]>('/admin/campaign-links')
      .then((r) => {
        setRows(r);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      await api.postJson('/admin/campaign-links', {
        slug: form.slug,
        destination_path: form.destination_path,
        utm_source: form.utm_source || undefined,
        utm_medium: form.utm_medium || undefined,
        utm_campaign: form.utm_campaign || undefined,
        utm_content: form.utm_content || undefined,
      });
      setForm(CAMPAIGN_FORM_EMPTY);
      load();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <p className="hint">Loading…</p>;

  return (
    <>
      <p className="hint">
        A named link for one outreach channel — a Facebook post, a Nextdoor comment, a flyer's QR
        code — so it's clear afterward which one actually brought people to the site.
      </p>
      <form onSubmit={(e) => void create(e)} style={{ marginBottom: 24 }}>
        <div className="row4">
          <label>
            Slug (the /go/ path)
            <input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="fb-roanoke"
              required
            />
          </label>
          <label>
            Destination path
            <input
              value={form.destination_path}
              onChange={(e) => setForm({ ...form, destination_path: e.target.value })}
              placeholder="/tx/cibolo"
              required
            />
          </label>
          <label>
            UTM source
            <input
              value={form.utm_source}
              onChange={(e) => setForm({ ...form, utm_source: e.target.value })}
              placeholder="facebook"
            />
          </label>
          <label>
            UTM medium
            <input
              value={form.utm_medium}
              onChange={(e) => setForm({ ...form, utm_medium: e.target.value })}
              placeholder="social"
            />
          </label>
          <label>
            UTM campaign
            <input
              value={form.utm_campaign}
              onChange={(e) => setForm({ ...form, utm_campaign: e.target.value })}
              placeholder="roanoke_launch"
            />
          </label>
          <label>
            UTM content
            <input
              value={form.utm_content}
              onChange={(e) => setForm({ ...form, utm_content: e.target.value })}
              placeholder="roanoke_yardsale_group"
            />
          </label>
        </div>
        {msg && <p className="error">{msg}</p>}
        <button type="submit" disabled={busy}>
          Create link
        </button>
      </form>
      {rows.length === 0 ? (
        <p className="empty">No campaign links yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Link</th>
                <th>Destination</th>
                <th>Source / medium / campaign</th>
                <th>Clicks</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <code>/go/{r.slug}</code>
                  </td>
                  <td>{r.destination_path}</td>
                  <td>{[r.utm_source, r.utm_medium, r.utm_campaign].filter(Boolean).join(' / ') || '—'}</td>
                  <td>{r.click_count}</td>
                  <td>{new Date(r.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

interface SponsorRow {
  id: string;
  name: string;
  headline: string;
  body: string | null;
  destination_url: string;
  in_results: boolean;
  in_newsletter: boolean;
  target_states: string[];
  target_zips: string[];
  target_center_zip: string | null;
  target_radius_mi: number | null;
  starts_on: string;
  ends_on: string | null;
  status: 'active' | 'paused' | 'ended' | 'pending_review';
  region_id: string | null;
  slot: 'top' | 'second';
  region_name: string | null;
  self_serve: boolean;
  media_kind: 'image' | 'video' | null;
  media_status: 'none' | 'processing' | 'ready' | 'failed';
  media_error: string | null;
  poster_url: string | null;
  impressions: number;
  clicks: number;
  newsletter_clicks: number;
  impressions_30d: number;
  clicks_30d: number;
  viewers_30d: number;
}

const SPONSOR_FORM_EMPTY = {
  name: '',
  headline: '',
  body: '',
  destination_url: '',
  in_results: true,
  in_newsletter: false,
  states: '',
  zips: '',
  centerZip: '',
  radius: '',
  region: '',
  slot: 'top',
  starts_on: '',
  ends_on: '',
};

/** "12 clicks / 480 views (2.5%)" -- guards the 0-views case instead of printing NaN%. */
function ctr(clicks: number, impressions: number): string {
  return impressions > 0 ? `${((clicks / impressions) * 100).toFixed(1)}%` : '—';
}

interface AdRegion {
  id: string;
  name: string;
  place_id: number;
  radius_mi: number;
  active: boolean;
  top: { id: string; name: string; status: string; ends_on: string | null } | null;
  second: { id: string; name: string; status: string; ends_on: string | null } | null;
}

interface PlaceHit {
  id: number;
  name: string;
  state: string;
  population: number;
}

interface AdPrices {
  top_cents: number;
  second_cents: number;
  grace_period_days: number;
}

const money = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
// Matches the SEO city page's own radius (src/server/seo/place.ts's CITY_RADIUS_MI /
// src/server/sponsors/regions.ts's DEFAULT_REGION_RADIUS_MI): a new region defaults to the same
// area its SEO page already covers.
const DEFAULT_REGION_RADIUS_MI = 25;

/** Inline form to add a prospect to an open slot's waitlist (a Brevo list) -- shown when no one
 * currently holds that slot. */
function WaitlistForm({ regionId, slot, onDone }: { regionId: string; slot: 'top' | 'second'; onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await api.postJson(`/admin/ad-regions/${regionId}/waitlist`, { slot, email, name });
      onDone();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Could not add them to the waitlist');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" style={{ width: 100 }} />
      <input
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        type="email"
        placeholder="Email"
        required
        style={{ width: 140 }}
      />
      <button type="submit" disabled={busy}>
        Add
      </button>
      <button type="button" className="link" onClick={onDone}>
        Cancel
      </button>
      {err && <p className="error" style={{ flexBasis: '100%', margin: 0 }}>{err}</p>}
    </form>
  );
}

/** Metro regions, what is open in each, and the owner's slot prices. */
function RegionsPanel(props: { regions: AdRegion[]; prices: AdPrices | null; canEditPrices: boolean; onChange: () => void }) {
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeHits, setPlaceHits] = useState<PlaceHit[]>([]);
  const [place, setPlace] = useState<PlaceHit | null>(null);
  const [radius, setRadius] = useState(String(DEFAULT_REGION_RADIUS_MI));
  const [top, setTop] = useState('');
  const [second, setSecond] = useState('');
  const [grace, setGrace] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState<string | null>(null);

  useEffect(() => {
    if (props.prices) {
      setTop(String(props.prices.top_cents / 100));
      setSecond(String(props.prices.second_cents / 100));
      setGrace(String(props.prices.grace_period_days));
    }
  }, [props.prices]);

  // The same real cities the SEO pages are built from (src/server/places), not a typed name --
  // an ad region is literally one of these, so it can't drift from what /va/roanoke already shows.
  useEffect(() => {
    if (place && placeQuery === `${place.name}, ${place.state}`) return; // just picked; don't re-search it
    const q = placeQuery.trim();
    if (q.length < 2) {
      setPlaceHits([]);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      api
        .get<PlaceHit[]>(`/admin/places-search?q=${encodeURIComponent(q)}`)
        .then((r) => live && setPlaceHits(r))
        .catch(() => live && setPlaceHits([]));
    }, 200);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [placeQuery, place]);

  async function savePrices(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      await api.putJson('/admin/ad-prices', {
        top_cents: Math.round(Number(top) * 100),
        second_cents: Math.round(Number(second) * 100),
        grace_period_days: Math.round(Number(grace)),
      });
      setMsg('Prices and grace period saved.');
      props.onChange();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Could not save the prices');
    } finally {
      setBusy(false);
    }
  }

  async function addRegion(e: React.FormEvent) {
    e.preventDefault();
    if (!place) {
      setMsg('Pick a city from the list first.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      await api.postJson('/admin/ad-regions', { place_id: place.id, radius_mi: Number(radius) });
      setPlaceQuery('');
      setPlace(null);
      setPlaceHits([]);
      setRadius(String(DEFAULT_REGION_RADIUS_MI));
      props.onChange();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Could not add the region');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(r: AdRegion) {
    setBusy(true);
    try {
      await api.patchJson(`/admin/ad-regions/${r.id}`, { active: !r.active });
      props.onChange();
    } finally {
      setBusy(false);
    }
  }

  const slotCell = (region: AdRegion, slot: 'top' | 'second') => {
    const held = region[slot];
    const cents = slot === 'top' ? props.prices?.top_cents : props.prices?.second_cents;
    const key = `${region.id}:${slot}`;
    if (held) {
      return (
        <>
          <strong>{held.name}</strong>
          <div className="hint">
            {held.status === 'paused' ? `paused, held ${props.prices ? `${props.prices.grace_period_days} days` : ''} more` : 'active'} ·{' '}
            {held.ends_on ? `until ${held.ends_on}` : 'ongoing'}
          </div>
        </>
      );
    }
    return (
      <>
        <span>Open{cents !== undefined ? ` — ${money(cents)}/mo` : ''}</span>
        <div>
          {waitlistOpen === key ? (
            <WaitlistForm regionId={region.id} slot={slot} onDone={() => setWaitlistOpen(null)} />
          ) : (
            <button type="button" className="link" onClick={() => setWaitlistOpen(key)}>
              Add to waitlist
            </button>
          )}
        </div>
      </>
    );
  };

  return (
    <section style={{ marginBottom: 24 }} aria-labelledby="regions-h">
      <h3 id="regions-h">Regions and slot prices</h3>
      <p className="hint">
        A sponsor holds one slot in one region exclusively, for as long as they keep paying. The top slot
        shows above the first result; the second shows after the third.
      </p>
      <form onSubmit={(e) => void savePrices(e)} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label>
          Top slot, $/month per region
          <input value={top} onChange={(e) => setTop(e.target.value)} inputMode="decimal" disabled={!props.canEditPrices} />
        </label>
        <label>
          Second slot, $/month per region
          <input value={second} onChange={(e) => setSecond(e.target.value)} inputMode="decimal" disabled={!props.canEditPrices} />
        </label>
        <label>
          Grace period for a late payment, days
          <input value={grace} onChange={(e) => setGrace(e.target.value)} inputMode="numeric" disabled={!props.canEditPrices} />
        </label>
        {props.canEditPrices ? (
          <button type="submit" disabled={busy}>
            Save prices
          </button>
        ) : (
          <span className="hint">Only an administrator can change prices.</span>
        )}
      </form>
      {msg && <p className="hint" role="status">{msg}</p>}

      {props.regions.length === 0 ? (
        <p className="empty">No regions yet. Search a city below to add one (its area matches that city's own map page, by default 25 miles).</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Region</th>
                <th>Area</th>
                <th>Top slot</th>
                <th>Second slot</th>
                <th>On</th>
              </tr>
            </thead>
            <tbody>
              {props.regions.map((r) => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.name}</strong>
                  </td>
                  <td>{r.radius_mi} mi radius</td>
                  <td>{slotCell(r, 'top')}</td>
                  <td>{slotCell(r, 'second')}</td>
                  <td>
                    <button disabled={busy} onClick={() => void toggle(r)}>
                      {r.active ? 'Switch off' : 'Switch on'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={(e) => void addRegion(e)} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 12 }}>
        <label style={{ position: 'relative' }}>
          Add a region — search for its city
          <input
            value={placeQuery}
            onChange={(e) => {
              setPlaceQuery(e.target.value);
              setPlace(null);
            }}
            placeholder="Roanoke"
            autoComplete="off"
          />
          {placeHits.length > 0 && !place && (
            <ul
              className="table"
              style={{ position: 'absolute', top: '100%', left: 0, zIndex: 5, background: 'var(--white)', maxHeight: 220, overflowY: 'auto', listStyle: 'none', margin: 0, padding: 0, minWidth: 220 }}
            >
              {placeHits.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="link"
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px' }}
                    onClick={() => {
                      setPlace(p);
                      setPlaceQuery(`${p.name}, ${p.state}`);
                      setPlaceHits([]);
                    }}
                  >
                    {p.name}, {p.state}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </label>
        <label>
          Radius (miles)
          <input value={radius} onChange={(e) => setRadius(e.target.value)} inputMode="numeric" required />
        </label>
        <button type="submit" disabled={busy || !place}>
          Add region
        </button>
      </form>
    </section>
  );
}

function SponsorsSection({ canEditPrices }: { canEditPrices: boolean }) {
  const [rows, setRows] = useState<SponsorRow[]>([]);
  const [regions, setRegions] = useState<AdRegion[]>([]);
  const [prices, setPrices] = useState<AdPrices | null>(null);
  const [confirmEnd, setConfirmEnd] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState(SPONSOR_FORM_EMPTY);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get<SponsorRow[]>('/admin/sponsors')
      .then((r) => {
        setRows(r);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    api.get<AdRegion[]>('/admin/ad-regions').then(setRegions).catch(() => {});
    api.get<AdPrices>('/admin/ad-prices').then(setPrices).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const list = (s: string) =>
    s
      .split(/[\s,]+/)
      .map((x) => x.trim())
      .filter(Boolean);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      await api.postJson('/admin/sponsors', {
        name: form.name,
        headline: form.headline,
        body: form.body || undefined,
        destination_url: form.destination_url,
        in_results: form.in_results,
        in_newsletter: form.in_newsletter,
        // A regional slot takes its area from the region; otherwise the custom targeting applies.
        ...(form.region
          ? { region_id: form.region, slot: form.slot, target_states: [], target_zips: [] }
          : {
              target_states: list(form.states),
              target_zips: list(form.zips),
              target_center_zip: form.centerZip.trim() || undefined,
              target_radius_mi: form.radius.trim() ? Number(form.radius) : undefined,
            }),
        starts_on: form.starts_on,
        ends_on: form.ends_on || undefined,
      });
      setForm(SPONSOR_FORM_EMPTY);
      load();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  // Video and GIF conversion runs in the background; keep refreshing while any is in progress.
  useEffect(() => {
    if (!rows.some((r) => r.media_status === 'processing')) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [rows, load]);

  async function uploadCreative(id: string, file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.postForm(`/admin/sponsors/${id}/creative`, fd);
      load();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'The upload failed');
    } finally {
      setBusy(false);
    }
  }

  async function removeCreative(id: string) {
    setBusy(true);
    try {
      await api.delete(`/admin/sponsors/${id}/creative`);
      load();
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(id: string, status: 'active' | 'paused' | 'ended') {
    setBusy(true);
    setMsg('');
    try {
      await api.patchJson(`/admin/sponsors/${id}`, { status });
      setConfirmEnd(null);
      load();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <p className="hint">Loading…</p>;

  return (
    <>
      <p className="hint">
        Sponsored placements are always labeled and never change how a sale ranks. A region/slot sponsor added
        below is billed by hand; one who checked out at /advertise is billed automatically by Stripe and starts as
        "Awaiting review" — approve it to make it live, or reject it to cancel its billing (a refund of the first
        charge, if you want one, is a manual step in the Stripe dashboard). Reporting is aggregate counts only. A
        newsletter "view" means the email was delivered, not opened.
      </p>
      <RegionsPanel regions={regions} prices={prices} canEditPrices={canEditPrices} onChange={load} />
      <form onSubmit={(e) => void create(e)} style={{ marginBottom: 24 }}>
        <h3>Add a sponsor</h3>
        <div className="row4">
          <label>
            Sponsor name
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </label>
          <label>
            Headline
            <input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} maxLength={120} required />
          </label>
          <label>
            Link (http/https)
            <input value={form.destination_url} onChange={(e) => setForm({ ...form, destination_url: e.target.value })} placeholder="https://" required />
          </label>
          <label>
            Short description (optional)
            <input value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} maxLength={300} />
          </label>
          <label>
            Where
            <select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}>
              <option value="">Custom (states, ZIPs or radius)</option>
              {regions
                .filter((r) => r.active)
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} — exclusive slot
                  </option>
                ))}
            </select>
          </label>
          {form.region ? (
            <label>
              Slot
              <select value={form.slot} onChange={(e) => setForm({ ...form, slot: e.target.value })}>
                <option value="top">Top{prices ? ` — ${money(prices.top_cents)}/month` : ''}</option>
                <option value="second">Second{prices ? ` — ${money(prices.second_cents)}/month` : ''}</option>
              </select>
            </label>
          ) : (
            <>
              <label>
                States (blank = everywhere)
                <input value={form.states} onChange={(e) => setForm({ ...form, states: e.target.value })} placeholder="VA, NC" />
              </label>
              <label>
                ZIPs (blank = everywhere)
                <input value={form.zips} onChange={(e) => setForm({ ...form, zips: e.target.value })} placeholder="24011 24012" />
              </label>
              <label>
                Radius: centre ZIP (optional)
                <input value={form.centerZip} onChange={(e) => setForm({ ...form, centerZip: e.target.value })} inputMode="numeric" maxLength={5} placeholder="24011" />
              </label>
              <label>
                Radius: miles around it
                <input value={form.radius} onChange={(e) => setForm({ ...form, radius: e.target.value })} inputMode="numeric" placeholder="30" />
              </label>
            </>
          )}
          <label>
            Starts
            <input type="date" value={form.starts_on} onChange={(e) => setForm({ ...form, starts_on: e.target.value })} required />
          </label>
          <label>
            Ends (blank = while they keep paying)
            <input type="date" value={form.ends_on} onChange={(e) => setForm({ ...form, ends_on: e.target.value })} />
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={form.in_results} onChange={(e) => setForm({ ...form, in_results: e.target.checked })} />
          Show in search results
        </label>
        <label className="check">
          <input type="checkbox" checked={form.in_newsletter} onChange={(e) => setForm({ ...form, in_newsletter: e.target.checked })} />
          Include in the weekly email
        </label>
        {msg && <p className="error">{msg}</p>}
        <button type="submit" disabled={busy}>
          Add sponsor
        </button>
      </form>
      {rows.length === 0 ? (
        <p className="empty">No sponsors yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Sponsor</th>
                <th>Artwork</th>
                <th>Where</th>
                <th>Dates</th>
                <th>Last 30 days</th>
                <th>All time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <strong>{r.name}</strong>
                    <div className="hint">{r.headline}</div>
                  </td>
                  <td>
                    {r.poster_url && r.media_status === 'ready' && (
                      <img src={r.poster_url} alt={`Artwork for ${r.name}`} style={{ width: 96, height: 'auto', display: 'block', marginBottom: 4 }} />
                    )}
                    <div className="hint">
                      {r.media_status === 'ready' && `${r.media_kind === 'video' ? 'Video' : 'Image'} ready`}
                      {r.media_status === 'processing' && 'Converting…'}
                      {r.media_status === 'failed' && `Failed: ${r.media_error ?? 'unknown error'}`}
                      {r.media_status === 'none' && 'Text only'}
                    </div>
                    <input
                      type="file"
                      aria-label={`Upload artwork for ${r.name}`}
                      accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
                      disabled={busy || r.media_status === 'processing'}
                      onChange={(e) => {
                        void uploadCreative(r.id, e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                    {r.media_status !== 'none' && r.media_status !== 'processing' && (
                      <button disabled={busy} onClick={() => void removeCreative(r.id)}>
                        Remove artwork
                      </button>
                    )}
                  </td>
                  <td>
                    {r.region_name ? (
                      <>
                        <strong>{r.region_name}</strong> · {r.slot} slot
                      </>
                    ) : r.target_states.length + r.target_zips.length === 0 && !r.target_center_zip ? (
                      'Everywhere'
                    ) : (
                      [
                        ...r.target_states,
                        ...r.target_zips,
                        ...(r.target_center_zip ? [`${r.target_center_zip} + ${r.target_radius_mi} mi`] : []),
                      ].join(', ')
                    )}
                    <div className="hint">{[r.in_results && 'results', r.in_newsletter && 'email'].filter(Boolean).join(' + ')}</div>
                  </td>
                  <td>
                    {r.starts_on} → {r.ends_on ?? 'ongoing'}
                  </td>
                  <td>
                    {r.impressions_30d} views · {r.clicks_30d} clicks ({ctr(r.clicks_30d, r.impressions_30d)})
                    <div className="hint">{r.viewers_30d} unique viewers</div>
                  </td>
                  <td>
                    {r.impressions} views · {r.clicks} clicks ({ctr(r.clicks, r.impressions)})
                    {r.newsletter_clicks > 0 && <div className="hint">{r.newsletter_clicks} from the email</div>}
                  </td>
                  <td>
                    <strong>{r.status === 'pending_review' ? 'Awaiting review' : r.status}</strong>
                    {r.self_serve && <div className="hint">Self-serve, billed by Stripe</div>}{' '}
                    {r.status === 'pending_review' ? (
                      <>
                        <button disabled={busy} onClick={() => void setStatus(r.id, 'active')}>
                          Approve
                        </button>{' '}
                        {confirmEnd === r.id ? (
                          <button disabled={busy} onClick={() => void setStatus(r.id, 'ended')}>
                            Confirm: reject and stop billing
                          </button>
                        ) : (
                          <button disabled={busy} onClick={() => setConfirmEnd(r.id)}>
                            Reject…
                          </button>
                        )}
                      </>
                    ) : (
                      r.status !== 'ended' && (
                        <>
                          <button disabled={busy} onClick={() => void setStatus(r.id, r.status === 'active' ? 'paused' : 'active')}>
                            {r.status === 'active' ? 'Pause' : 'Resume'}
                          </button>{' '}
                          {confirmEnd === r.id ? (
                            <button disabled={busy} onClick={() => void setStatus(r.id, 'ended')}>
                              Confirm: end and free the slot
                            </button>
                          ) : (
                            <button disabled={busy} onClick={() => setConfirmEnd(r.id)}>
                              End…
                            </button>
                          )}
                        </>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

interface SignalRow {
  id: string;
  email: string;
  postal_code: string;
  radius_mi: number;
  updated_at: string;
  nearby_count: number;
  thin: boolean;
}

function SignalsSection() {
  const [rows, setRows] = useState<SignalRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    api
      .get<SignalRow[]>('/admin/subscriptions')
      .then((r) => {
        setRows(r);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function dismiss(id: string) {
    setBusy(id);
    try {
      await api.postJson(`/admin/subscriptions/${id}/dismiss`);
      load();
    } finally {
      setBusy(null);
    }
  }

  if (!loaded) return <p className="hint">Loading…</p>;

  return (
    <>
      <p className="hint">
        New subscribers whose area has thin or empty coverage — a signal it's worth a listings pull
        there. Dismiss once you've handled it; it reappears if that subscriber changes their ZIP or
        radius.
      </p>
      {rows.length === 0 ? (
        <p className="empty">
          No open signals — every recent subscriber landed somewhere with decent coverage.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Subscriber</th>
                <th>ZIP / radius</th>
                <th>Nearby listings</th>
                <th>Subscribed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.email}</td>
                  <td>
                    {r.postal_code} · {r.radius_mi}mi
                  </td>
                  <td>
                    <strong style={{ color: r.thin ? '#b91c1c' : undefined }}>
                      {r.nearby_count} {r.nearby_count === 1 ? 'listing' : 'listings'}
                    </strong>
                    {r.thin && <span className="hint"> — thin coverage</span>}
                  </td>
                  <td>{new Date(r.updated_at).toLocaleDateString()}</td>
                  <td>
                    <button disabled={busy === r.id} onClick={() => void dismiss(r.id)}>
                      Dismiss
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

interface ListingBoostSettings {
  secret_key_set: boolean;
  publishable_key: string;
  webhook_secret_set: boolean;
  ads_webhook_secret_set: boolean;
  price_residential_cents: number;
  price_commercial_cents: number;
}

function ListingBoostSection() {
  const [settings, setSettings] = useState<ListingBoostSettings | null>(null);
  const [secretKeyInput, setSecretKeyInput] = useState('');
  const [publishableKeyInput, setPublishableKeyInput] = useState('');
  const [webhookSecretInput, setWebhookSecretInput] = useState('');
  const [adsWebhookSecretInput, setAdsWebhookSecretInput] = useState('');
  const [priceResidential, setPriceResidential] = useState('5');
  const [priceCommercial, setPriceCommercial] = useState('100');
  const [saveMsg, setSaveMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api
      .get<ListingBoostSettings>('/admin/settings/listing-boost')
      .then((s) => {
        setSettings(s);
        setPublishableKeyInput(s.publishable_key);
        setPriceResidential((s.price_residential_cents / 100).toFixed(0));
        setPriceCommercial((s.price_commercial_cents / 100).toFixed(0));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveMsg('');
    try {
      await api.putJson('/admin/settings/listing-boost', {
        ...(secretKeyInput ? { secret_key: secretKeyInput } : {}),
        publishable_key: publishableKeyInput,
        ...(webhookSecretInput ? { webhook_secret: webhookSecretInput } : {}),
        ...(adsWebhookSecretInput ? { ads_webhook_secret: adsWebhookSecretInput } : {}),
        price_residential_cents: Math.round(Number(priceResidential) * 100),
        price_commercial_cents: Math.round(Number(priceCommercial) * 100),
      });
      setSecretKeyInput('');
      setWebhookSecretInput('');
      setAdsWebhookSecretInput('');
      setSaveMsg('Saved.');
      load();
    } catch {
      setSaveMsg('Could not save settings.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card">
      <h2>Payment settings</h2>
      <form onSubmit={(e) => void save(e)}>
        <label htmlFor="lb-secret">
          Secret key{' '}
          {settings?.secret_key_set && (
            <span className="hint">(currently set — leave blank to keep it)</span>
          )}
        </label>
        <input
          id="lb-secret"
          type="password"
          placeholder={settings?.secret_key_set ? '••••••••' : 'sk_live_...'}
          value={secretKeyInput}
          onChange={(e) => setSecretKeyInput(e.target.value)}
        />
        <label htmlFor="lb-pub">Publishable key</label>
        <input
          id="lb-pub"
          value={publishableKeyInput}
          onChange={(e) => setPublishableKeyInput(e.target.value)}
        />
        <label htmlFor="lb-webhook">
          Webhook signing secret{' '}
          {settings?.webhook_secret_set && (
            <span className="hint">(currently set — leave blank to keep it)</span>
          )}
        </label>
        <input
          id="lb-webhook"
          type="password"
          placeholder={settings?.webhook_secret_set ? '••••••••' : 'whsec_...'}
          value={webhookSecretInput}
          onChange={(e) => setWebhookSecretInput(e.target.value)}
        />
        <p className="hint">
          For featured listings. Its Stripe endpoint URL is <code>/api/v1/webhooks/stripe-listing-boost</code>.
        </p>
        <label htmlFor="lb-ads-webhook">
          Ad slot webhook signing secret{' '}
          {settings?.ads_webhook_secret_set && (
            <span className="hint">(currently set — leave blank to keep it)</span>
          )}
        </label>
        <input
          id="lb-ads-webhook"
          type="password"
          placeholder={settings?.ads_webhook_secret_set ? '••••••••' : 'whsec_...'}
          value={adsWebhookSecretInput}
          onChange={(e) => setAdsWebhookSecretInput(e.target.value)}
        />
        <p className="hint">
          A separate Stripe endpoint, since Stripe signs each endpoint with its own secret: add{' '}
          <code>/api/v1/webhooks/stripe-ads</code> in the Stripe dashboard, listening for{' '}
          <code>checkout.session.completed</code>, <code>checkout.session.async_payment_succeeded</code>,{' '}
          <code>invoice.payment_failed</code>, <code>invoice.payment_succeeded</code> and{' '}
          <code>customer.subscription.deleted</code>.
        </p>
        <div className="row3">
          <div>
            <label htmlFor="lb-price-res">Residential price ($, one-time)</label>
            <input
              id="lb-price-res"
              type="number"
              min={1}
              value={priceResidential}
              onChange={(e) => setPriceResidential(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="lb-price-com">Commercial price ($, one-time)</label>
            <input
              id="lb-price-com"
              type="number"
              min={1}
              value={priceCommercial}
              onChange={(e) => setPriceCommercial(e.target.value)}
            />
          </div>
        </div>
        {saveMsg && <p role="status">{saveMsg}</p>}
        <button className="primary" type="submit" disabled={saving}>
          Save
        </button>
      </form>
    </section>
  );
}

interface AnalyticsData {
  days: number;
  total: number;
  unique_visitors: number;
  by_name: { event_name: string; count: number }[];
  by_day: { day: string; count: number }[];
  by_campaign: { utm_campaign: string | null; count: number }[];
  top_links: {
    kind: 'share' | 'campaign';
    slug: string | null;
    code: string | null;
    destination_path: string;
    utm_campaign: string | null;
    click_count: number;
    created_at: string;
  }[];
}

const EVENT_NAME_LABEL: Record<string, string> = {
  landing_page_view: 'Landing page views',
  campaign_link_click: 'Campaign link clicks',
  map_loaded: 'Map loads',
  location_selected: 'Locations searched',
  radius_changed: 'Radius changed',
  search_area_clicked: '"Search this area" clicks',
  filter_changed: 'Filters changed',
  listing_opened: 'Listings opened',
  route_started: 'Routes started',
  route_built: 'Routes built',
  external_navigation_clicked: 'Sent to Google Maps',
  sale_submitted: 'Sales submitted',
  submission_failed: 'Submissions failed',
  newsletter_signup_started: 'Newsletter signups started',
  newsletter_signup_confirmed: 'Newsletter signups confirmed',
  newsletter_link_clicked: 'Newsletter link clicks',
  coverage_requested: 'Coverage requests',
  share_link_created: 'Share links created',
  share_link_clicked: 'Share links clicked',
  sponsor_impression: 'Sponsor impressions',
  sponsor_click: 'Sponsor clicks',
};

/** First-party site analytics (spec §18): traffic totals, what visitors are doing, and which
 * named campaigns (utm_campaign, e.g. a /go/:slug link someone posted) are actually driving it.
 * No third-party analytics script runs on the site -- this reads the site's own event log. */
function AnalyticsSection() {
  const [days, setDays] = useState<7 | 30 | 90>(7);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback((d: 7 | 30 | 90) => {
    setLoaded(false);
    api
      .get<AnalyticsData>(`/admin/analytics?days=${d}`)
      .then((r) => {
        setData(r);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  if (!loaded) return <p className="hint">Loading…</p>;
  if (!data) return <p className="empty">Couldn't load analytics.</p>;

  const maxDay = Math.max(1, ...data.by_day.map((d) => d.count));

  return (
    <>
      <p className="hint">
        First-party only — read from the site's own event log, never a third-party tracker. Counts are
        for the window below; "unique visitors" counts distinct anonymous browser IDs, never an email or
        address.
      </p>
      <div className="row3" style={{ marginBottom: 16 }}>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            aria-pressed={days === d}
            onClick={() => setDays(d as 7 | 30 | 90)}
            style={days === d ? { fontWeight: 700 } : undefined}
          >
            Last {d} days
          </button>
        ))}
      </div>

      <div className="row3" style={{ marginBottom: 16 }}>
        <div>
          <div className="hint">Total events</div>
          <strong style={{ fontSize: '1.5rem' }}>{data.total.toLocaleString()}</strong>
        </div>
        <div>
          <div className="hint">Unique visitors</div>
          <strong style={{ fontSize: '1.5rem' }}>{data.unique_visitors.toLocaleString()}</strong>
        </div>
        <div>
          <div className="hint">Sales submitted</div>
          <strong style={{ fontSize: '1.5rem' }}>
            {(data.by_name.find((n) => n.event_name === 'sale_submitted')?.count ?? 0).toLocaleString()}
          </strong>
        </div>
      </div>

      <h3>Daily activity</h3>
      {data.by_day.length === 0 ? (
        <p className="empty">Nothing recorded yet in this window.</p>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80, marginBottom: 8 }}>
          {data.by_day.map((d) => (
            <div
              key={d.day}
              title={`${d.day}: ${d.count}`}
              style={{
                flex: 1,
                minWidth: 4,
                height: `${Math.max(4, (d.count / maxDay) * 80)}px`,
                background: 'var(--brand-600)',
                borderRadius: 2,
              }}
            />
          ))}
        </div>
      )}

      <h3>By event type</h3>
      {data.by_name.length === 0 ? (
        <p className="empty">Nothing recorded yet in this window.</p>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: 24 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {data.by_name.map((n) => (
                <tr key={n.event_name}>
                  <td>{EVENT_NAME_LABEL[n.event_name] ?? n.event_name}</td>
                  <td>{n.count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>By campaign</h3>
      {data.by_campaign.length === 0 ? (
        <p className="empty">No tracked campaign activity in this window.</p>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: 24 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Events</th>
              </tr>
            </thead>
            <tbody>
              {data.by_campaign.map((c) => (
                <tr key={c.utm_campaign}>
                  <td>{c.utm_campaign}</td>
                  <td>{c.count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Top links (all time)</h3>
      {data.top_links.length === 0 ? (
        <p className="empty">No link clicks yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Link</th>
                <th>Goes to</th>
                <th>Clicks</th>
              </tr>
            </thead>
            <tbody>
              {data.top_links.map((l) => (
                <tr key={`${l.kind}-${l.slug ?? l.code}`}>
                  <td>
                    <code>/{l.kind === 'campaign' ? 'go' : 'r'}/{l.slug ?? l.code}</code>
                  </td>
                  <td className="hint">{l.destination_path}</td>
                  <td>{l.click_count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
